
/* File: frontend/src/modules/trading/dispatch_order/pages/TradingDispatchOrderCreatePage.js */
import axiosClient from "../../../../api/axiosClient";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingDispatchOrderService from "../services/tradingDispatchOrderService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  return String(value).slice(0, 10);
};

const formatQuantity = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  }) : "-";
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #d1d5db",
  borderRadius: "7px",
  fontSize: "13px",
  boxSizing: "border-box",
  background: "#fff",
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
};

const primaryButtonStyle = {
  padding: "10px 18px",
  border: "none",
  borderRadius: "7px",
  background: "#5b4bdb",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "10px 18px",
  border: "1px solid #d1d5db",
  borderRadius: "7px",
  background: "#fff",
  color: "#374151",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "20px",
  marginBottom: "18px",
};

const tableHeaderStyle = {
  padding: "12px 10px",
  textAlign: "left",
  background: "#f8fafc",
  color: "#475569",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #f1f5f9",
  fontSize: "13px",
  color: "#374151",
  verticalAlign: "middle",
};

const sectionTitleStyle = {
  margin: "0 0 16px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#111827",
};

const getSalesOrderCustomer = (order) =>
  order?.customer_name ||
  order?.business_partner_name ||
  order?.customer?.name ||
  order?.business_partner?.name ||
  (order?.business_partner_id
    ? `Business Partner #${order.business_partner_id}`
    : "-");

const TradingDispatchOrderCreatePage = () => {
  const navigate = useNavigate();

  const [salesOrders, setSalesOrders] = useState([]);
  const [salesOrderSearch, setSalesOrderSearch] = useState("");
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState("");

  const [dispatchItems, setDispatchItems] = useState([]);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);

  const [plannedDispatchDate, setPlannedDispatchDate] = useState(getTodayDate());
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [transporterBookingRequired, setTransporterBookingRequired] =
    useState(false);
  const [transporterBpId, setTransporterBpId] = useState("");
  const [transporters, setTransporters] = useState([]);
  const [loadingTransporters, setLoadingTransporters] = useState(false);

  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [remarks, setRemarks] = useState("");

  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
    const [itemError, setItemError] = useState("");
    const [info, setInfo] = useState("");

  const loadTransporters = useCallback(async () => {
  setLoadingTransporters(true);

  try {
    const response = await axiosClient.get(
      "/api/ppe/transport/transporters/search"
    );

    const data = getResponseData(response);
    setTransporters(Array.isArray(data) ? data : []);
  } catch (err) {
    setError(
      getErrorMessage(err, "Unable to load transporters.")
    );
  } finally {
    setLoadingTransporters(false);
  }
}, []);
  
    const loadSalesOrders = useCallback(async () => {
    setLoadingOrders(true);
    setError("");

    try {
      const response =
        await tradingDispatchOrderService.getEligibleSalesOrders();

      const data = getResponseData(response);
      const rows = Array.isArray(data)
        ? data
        : data?.sales_orders || data?.items || data?.results || [];

      setSalesOrders(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to load eligible Sales Orders.")
      );
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  useEffect(() => {
  loadTransporters();
}, [loadTransporters]);

  const filteredSalesOrders = useMemo(() => {
    const search = salesOrderSearch.trim().toLowerCase();

    if (!search) return salesOrders;

    return salesOrders.filter((order) => {
      const searchableText = [
        order.sales_order_no,
        getSalesOrderCustomer(order),
        order.status_code,
        order.delivery_address,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(search);
    });
  }, [salesOrders, salesOrderSearch]);

  const handleSelectSalesOrder = async (order) => {
    if (!order?.id) {
      setError("The selected Sales Order does not have a valid ID.");
      return;
    }

    setSelectedSalesOrderId(String(order.id));
    setSelectedSalesOrder(order);
    setDispatchItems([]);
    setDeliveryAddress(order.delivery_address || "");
    setError("");
    setItemError("");
    setInfo("");
    setLoadingItems(true);

    try {
      const response =
        await tradingDispatchOrderService.getDispatchableSalesOrderItems(
          order.id
        );

      const data = getResponseData(response);
      const items = Array.isArray(data?.items) ? data.items : [];

      setDispatchItems(
        items.map((item) => ({
          ...item,
          planned_dispatch_quantity: "",
        }))
      );

      if (items.length === 0) {
        setInfo(
          "This Sales Order has no remaining quantity available for dispatch."
        );
      }
    } catch (err) {
  setDispatchItems([]);
  setItemError(
    getErrorMessage(err, "Unable to load dispatchable Sales Order items.")
  );
} finally {
      setLoadingItems(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    setDispatchItems((currentItems) =>
      currentItems.map((item) =>
        String(item.sales_order_item_id) === String(itemId)
          ? { ...item, planned_dispatch_quantity: value }
          : item
      )
    );
  };

  const plannedItems = useMemo(
    () =>
      dispatchItems
        .map((item) => ({
          ...item,
          planned_dispatch_quantity: Number(
            item.planned_dispatch_quantity || 0
          ),
        }))
        .filter((item) => item.planned_dispatch_quantity > 0),
    [dispatchItems]
  );

  const totalPlannedQuantity = useMemo(
    () =>
      plannedItems.reduce(
        (total, item) => total + item.planned_dispatch_quantity,
        0
      ),
    [plannedItems]
  );

  const validateForm = () => {
    if (!selectedSalesOrderId || !selectedSalesOrder) {
      return "Please select a Sales Order.";
    }

    if (!plannedDispatchDate) {
      return "Please select the Planned Dispatch Date.";
    }

    if (plannedItems.length === 0) {
      return "Enter a planned dispatch quantity for at least one item.";
    }

    for (const item of plannedItems) {
      const quantity = item.planned_dispatch_quantity;
      const remaining = Number(item.remaining_quantity ?? 0);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid quantity for ${item.item_description || "the item"}.`;
      }

      if (quantity > remaining) {
        return `Planned quantity for ${
          item.item_description || "the item"
        } cannot exceed the remaining quantity of ${remaining}.`;
      }
    }

    if (transporterBookingRequired) {
      if (
        !transporterBpId ||
        !Number.isInteger(Number(transporterBpId)) ||
        Number(transporterBpId) <= 0
      ) {
        return "Enter a valid Transporter Business Partner ID.";
      }
    }

    return "";
  };

  const handleSave = async () => {
    setError("");
    setInfo("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      sales_order_id: Number(selectedSalesOrderId),
      planned_dispatch_date: plannedDispatchDate,
      delivery_address: deliveryAddress.trim(),
      transporter_booking_required: transporterBookingRequired,
      transporter_bp_id: transporterBookingRequired
        ? Number(transporterBpId)
        : null,
      delivery_instructions: deliveryInstructions.trim(),
      remarks: remarks.trim(),
      items: plannedItems.map((item) => ({
        sales_order_item_id: Number(item.sales_order_item_id),
        planned_dispatch_quantity: item.planned_dispatch_quantity,
      })),
    };

    setSaving(true);

    try {
      const response =
        await tradingDispatchOrderService.createDispatchOrder(payload);

      const data = getResponseData(response);
      const dispatchOrder =
        data?.dispatch_order || data?.data?.dispatch_order || data;

      const dispatchOrderId =
        dispatchOrder?.id ||
        dispatchOrder?.dispatch_order_id ||
        data?.dispatch_order_id;

      if (dispatchOrderId) {
        navigate(`/trading/dispatch-orders/${dispatchOrderId}`);
      } else {
        setInfo(
          "Dispatch Order saved. The API response did not include its ID, so the detail page could not be opened."
        );
      }
    } catch (err) {
  const message = getErrorMessage(
    err,
    "Unable to create the Dispatch Order."
  );

  if (/quantity|dispatchable/i.test(message)) {
    setItemError(message);
    setError("");
  } else {
    setError(message);
  }
} finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        background: "#f8fafc",
        minHeight: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "22px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "6px",
            }}
          >
            Trading / Dispatch Orders / Create
          </div>

          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Create Dispatch Order
          </h2>

          <p
            style={{
              margin: "7px 0 0",
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            Plan dispatch quantities against the remaining Sales Order items.
          </p>
        </div>

        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate("/trading/dispatch-orders")}
          disabled={saving}
        >
          Back to Dispatch Orders
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {info && (
        <div
          role="status"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            fontSize: "13px",
          }}
        >
          {info}
        </div>
      )}

      {/* Section 1: Select Sales Order */}
      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>1. Select Sales Order</h3>

        <div style={{ marginBottom: "15px", maxWidth: "420px" }}>
          <label style={labelStyle} htmlFor="sales-order-search">
            Search Sales Orders
          </label>

          <input
            id="sales-order-search"
            type="text"
            value={salesOrderSearch}
            onChange={(event) => setSalesOrderSearch(event.target.value)}
            placeholder="Search by order number or customer..."
            style={inputStyle}
          />
        </div>

        {loadingOrders ? (
          <div style={{ padding: "24px", color: "#64748b", fontSize: "13px" }}>
            Loading eligible Sales Orders...
          </div>
        ) : filteredSalesOrders.length === 0 ? (
          <div style={{ padding: "24px", color: "#64748b", fontSize: "13px" }}>
            No eligible Sales Orders found.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "760px",
              }}
            >
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Select</th>
                  <th style={tableHeaderStyle}>Sales Order No.</th>
                  <th style={tableHeaderStyle}>Customer</th>
                  <th style={tableHeaderStyle}>Order Date</th>
                  <th style={tableHeaderStyle}>Requested Delivery</th>
                  <th style={tableHeaderStyle}>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredSalesOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => handleSelectSalesOrder(order)}
                    style={{
                      cursor: "pointer",
                      background:
                        String(selectedSalesOrderId) === String(order.id)
                          ? "#f5f3ff"
                          : "#fff",
                    }}
                  >
                    <td style={tableCellStyle}>
                      <input
                        type="radio"
                        name="selectedSalesOrder"
                        checked={
                          String(selectedSalesOrderId) === String(order.id)
                        }
                        onChange={() => handleSelectSalesOrder(order)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Select Sales Order ${order.sales_order_no}`}
                      />
                    </td>

                    <td
                      style={{
                        ...tableCellStyle,
                        fontWeight: 700,
                        color: "#5b4bdb",
                      }}
                    >
                      {order.sales_order_no || `SO-${order.id}`}
                    </td>

                    <td style={tableCellStyle}>
                      {getSalesOrderCustomer(order)}
                    </td>

                    <td style={tableCellStyle}>
                      {formatDate(order.sales_order_date)}
                    </td>

                    <td style={tableCellStyle}>
                      {formatDate(order.requested_delivery_date)}
                    </td>

                    <td style={tableCellStyle}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "5px",
                          background: "#f1f5f9",
                          color: "#475569",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {order.status_code || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section 2: Dispatch Items */}
      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>2. Dispatch Items</h3>
        {itemError && (
            <div
                role="alert"
                style={{
                padding: "12px 15px",
                borderRadius: "8px",
                marginBottom: "16px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                fontSize: "13px",
                }}
            >
                <strong>Quantity validation</strong>
                <div style={{ marginTop: "4px" }}>{itemError}</div>
            </div>
            )}

        {!selectedSalesOrder ? (
          <div style={{ color: "#64748b", fontSize: "13px" }}>
            Select a Sales Order above to load its remaining dispatchable items.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                marginBottom: "16px",
                fontSize: "13px",
              }}
            >
              <div>
                <span style={{ color: "#64748b" }}>Sales Order: </span>
                <strong>
                  {selectedSalesOrder.sales_order_no ||
                    `SO-${selectedSalesOrder.id}`}
                </strong>
              </div>

              <div>
                <span style={{ color: "#64748b" }}>Customer: </span>
                <strong>{getSalesOrderCustomer(selectedSalesOrder)}</strong>
              </div>
            </div>

            {loadingItems ? (
              <div
                style={{ padding: "20px", color: "#64748b", fontSize: "13px" }}
              >
                Loading dispatchable items...
              </div>
            ) : dispatchItems.length === 0 ? (
              <div
                style={{ padding: "20px", color: "#64748b", fontSize: "13px" }}
              >
                No remaining quantities are available for dispatch.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: "850px",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Line</th>
                      <th style={tableHeaderStyle}>Item Description</th>
                      <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                        Ordered Qty
                      </th>
                      <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                        Already Dispatched
                      </th>
                      <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                        Already Planned Qty
                      </th>
                      <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                        Remaining Qty
                      </th>

                      <th style={{ ...tableHeaderStyle, minWidth: "160px" }}>
                        Planned Dispatch Qty
                        </th>
                        
                    </tr>
                  </thead>

                  <tbody>
                    {dispatchItems.map((item) => (
                      <tr key={item.sales_order_item_id}>
                        <td style={tableCellStyle}>
                          {item.item_seq_no ?? "-"}
                        </td>

                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: 600 }}>
                            {item.item_description || "-"}
                          </div>
                          {item.raw_material_id && (
                            <div
                              style={{
                                marginTop: "4px",
                                color: "#64748b",
                                fontSize: "11px",
                              }}
                            >
                              Item ID: {item.raw_material_id}
                            </div>
                          )}
                        </td>

                        <td
                          style={{ ...tableCellStyle, textAlign: "right" }}
                        >
                          {formatQuantity(item.ordered_quantity)}
                        </td>

                        {/* Already Dispatched */}
                        <td style={{ ...tableCellStyle, textAlign: "right" }}>
                          {formatQuantity(item.dispatched_quantity)}
                        </td>

                        {/* Already Planned Qty */}
                        <td style={{ ...tableCellStyle, textAlign: "right" }}>
                          {formatQuantity(item.reserved_quantity)}
                        </td>

                        {/* Remaining Qty */}
                        <td
                          style={{
                            ...tableCellStyle,
                            textAlign: "right",
                            fontWeight: 700,
                          }}
                        >
                          {formatQuantity(item.remaining_quantity)}
                        </td>

                        <td style={tableCellStyle}>
                          <input
                            type="number"
                            min="0"
                            max={Number(item.remaining_quantity ?? 0)}
                            step="0.001"
                            value={item.planned_dispatch_quantity}
                            onChange={(event) =>
                              handleQuantityChange(
                                item.sales_order_item_id,
                                event.target.value
                              )
                            }
                            style={inputStyle}
                            aria-label={`Planned dispatch quantity for ${item.item_description || "item"}`}
                            disabled={
                              Number(item.remaining_quantity ?? 0) <= 0
                            }
                          />
                          {Number(item.planned_dispatch_quantity || 0) >
                            Number(item.remaining_quantity ?? 0) && (
                            <div
                              style={{
                                color: "#b91c1c",
                                fontSize: "11px",
                                marginTop: "4px",
                              }}
                            >
                              Exceeds remaining quantity
                            </div>
                          )}
                        </td>
                        
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                padding: "12px 14px",
                background: "#f8fafc",
                borderRadius: "7px",
                fontSize: "13px",
              }}
            >
              <span style={{ color: "#64748b", marginRight: "10px" }}>
                Total Planned Quantity
              </span>
              <strong>{formatQuantity(totalPlannedQuantity)}</strong>
            </div>
          </>
        )}
      </section>

      {/* Section 3: Dispatch Details */}
      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>3. Dispatch Details</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "18px",
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="planned-dispatch-date">
              Planned Dispatch Date *
            </label>
            <input
              id="planned-dispatch-date"
              type="date"
              value={plannedDispatchDate}
              onChange={(event) =>
                setPlannedDispatchDate(event.target.value)
              }
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="transporter-booking-required">
              Transporter Booking Required
            </label>
            <select
              id="transporter-booking-required"
              value={transporterBookingRequired ? "YES" : "NO"}
              onChange={(event) => {
                const required = event.target.value === "YES";
                setTransporterBookingRequired(required);
                if (!required) setTransporterBpId("");
              }}
              style={inputStyle}
            >
              <option value="NO">No</option>
              <option value="YES">Yes</option>
            </select>
          </div>

          
{transporterBookingRequired && (
  <div>
    <label style={labelStyle} htmlFor="transporter-bp-id">
      Transporter *
    </label>

    <select
      id="transporter-bp-id"
      value={transporterBpId}
      onChange={(event) => setTransporterBpId(event.target.value)}
      style={inputStyle}
      required
      disabled={loadingTransporters}
    >
      <option value="">
        {loadingTransporters
          ? "Loading transporters..."
          : "Select Transporter"}
      </option>

      {transporters.map((transporter) => (
        <option key={transporter.id} value={transporter.id}>
          {transporter.name}
          {transporter.code ? ` (${transporter.code})` : ""}
        </option>
      ))}
    </select>

    {!loadingTransporters && transporters.length === 0 && (
      <div
        style={{
          marginTop: "5px",
          color: "#b91c1c",
          fontSize: "11px",
        }}
      >
        No transporters found.
      </div>
    )}
  </div>
)}

        </div>

        <div style={{ marginTop: "18px" }}>
          <label style={labelStyle} htmlFor="delivery-address">
            Delivery Address
          </label>
          <textarea
            id="delivery-address"
            rows={3}
            value={deliveryAddress}
            onChange={(event) => setDeliveryAddress(event.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Delivery address"
          />
        </div>

        <div style={{ marginTop: "18px" }}>
          <label style={labelStyle} htmlFor="delivery-instructions">
            Delivery Instructions
          </label>
          <textarea
            id="delivery-instructions"
            rows={2}
            value={deliveryInstructions}
            onChange={(event) => setDeliveryInstructions(event.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Special handling or delivery instructions"
          />
        </div>

        <div style={{ marginTop: "18px" }}>
          <label style={labelStyle} htmlFor="dispatch-remarks">
            Remarks
          </label>
          <textarea
            id="dispatch-remarks"
            rows={2}
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
            placeholder="Optional remarks"
          />
        </div>
      </section>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          flexWrap: "wrap",
          paddingBottom: "24px",
        }}
      >
        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate("/trading/dispatch-orders")}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          type="button"
          style={{
            ...primaryButtonStyle,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={handleSave}
          disabled={saving || loadingItems}
        >
          {saving ? "Saving..." : "Save Dispatch Order"}
        </button>
      </div>
    </div>
  );
};

export default TradingDispatchOrderCreatePage;
